package com.tovtalk.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Button
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.unit.dp
import com.lomdaat.ads.model.VideoAd
import com.tovtalk.domain.repository.AdsRepository
import com.tovtalk.utils.logger.Logger
import com.tovtalk.utils.strings.StringKeys
import com.tovtalk.utils.strings.StringProvider
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

private sealed interface AdState {
    data object Loading : AdState
    data class Loaded(val ad: VideoAd) : AdState
    data object Unavailable : AdState
}

@Composable
fun SponsoredVideoReel(
    slotIndex: Int,
    adsRepository: AdsRepository,
    isVisible: Boolean,
    muted: Boolean,
    onAdvance: () -> Unit,
    modifier: Modifier = Modifier,
) {
    var state by remember(slotIndex) { mutableStateOf<AdState>(AdState.Loading) }

    LaunchedEffect(slotIndex) {
        val ad = adsRepository.fetchVerticalVideoAd()
        state = if (ad != null && ad.mediaUrl.isNotBlank()) AdState.Loaded(ad) else AdState.Unavailable
        Logger.d("SponsoredVideoReel slot=$slotIndex state=$state", "ADS")
    }

    Box(modifier = modifier.fillMaxSize().background(Color.Black), contentAlignment = Alignment.Center) {
        when (val s = state) {
            is AdState.Loading -> Unit // black frame while fetching
            is AdState.Unavailable -> LaunchedEffect(Unit) { onAdvance() }
            is AdState.Loaded -> LoadedAd(
                ad = s.ad,
                isVisible = isVisible,
                muted = muted,
                onAdvance = onAdvance,
                adsRepository = adsRepository,
            )
        }
    }
}

@Composable
private fun LoadedAd(
    ad: VideoAd,
    isVisible: Boolean,
    muted: Boolean,
    onAdvance: () -> Unit,
    adsRepository: AdsRepository,
) {
    val uriHandler = LocalUriHandler.current
    val scope = rememberCoroutineScope()
    var impressionFired by remember(ad.mediaUrl) { mutableStateOf(false) }
    var elapsedMs by remember(ad.mediaUrl) { mutableStateOf(0L) }

    // Fire impression once, when this ad becomes visible.
    LaunchedEffect(isVisible, ad.mediaUrl) {
        if (isVisible && !impressionFired) {
            impressionFired = true
            ad.impressionUrl?.let { fireAndForget(scope, adsRepository, it) }
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        TovTalkReelPlayer(
            url = ad.mediaUrl,
            isPlaying = isVisible,
            muted = muted,
            loop = false,
            onVideoEnded = onAdvance,
            onPositionUpdate = { positionMs, _ -> elapsedMs = positionMs },
        )

        // "Sponsored" label
        Text(
            text = StringProvider.get(StringKeys.SPONSORED),
            color = Color.White,
            modifier = Modifier.align(Alignment.TopStart).padding(16.dp),
        )

        // CTA button — opens clickThrough externally + fires click tracking.
        ad.clickThroughUrl?.let { target ->
            Button(
                onClick = {
                    ad.clickTrackingUrl?.let { fireAndForget(scope, adsRepository, it) }
                    runCatching { uriHandler.openUri(target) }
                },
                modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 96.dp),
            ) {
                Text(StringProvider.get(StringKeys.AD_LEARN_MORE))
            }
        }

        // Skip button — appears after skipOffset (default 5s if not provided).
        val skipAfter = ad.skipOffsetMs ?: 5_000L
        if (elapsedMs >= skipAfter) {
            Button(
                onClick = onAdvance,
                modifier = Modifier.align(Alignment.BottomEnd).padding(16.dp),
            ) {
                Text(StringProvider.get(StringKeys.AD_SKIP))
            }
        }
    }
}

private fun fireAndForget(scope: CoroutineScope, adsRepository: AdsRepository, url: String) {
    scope.launch { runCatching { adsRepository.trackVideoAdEvent(url) } }
}
